import { ErrorNotice } from "../ui/ErrorNotice.js";
/**
 * CommentsThread — review comments on a template (item 5C).
 *
 * Reads the thread via `client.listComments(templateId)` and posts new ones via
 * `client.addComment`. Each comment shows a public/internal badge; the add form
 * lets an admin choose the visibility (the server enforces that `internal`
 * requires an admin role, so non-admins only see/post `public`).
 *
 * Pure wiring of existing client methods — no new endpoints, no mock data. The
 * list degrades to an empty/error state if the read fails (offline/demo).
 */
import { useCallback, useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import { useSessionStore } from "../stores/session.js";
import { serverTsDate } from "@genz/platform";
import type { CommentType, ReviewComment } from "@genz/platform";

export interface CommentsThreadProps {
  templateId: string;
  /** Bumped by the parent (e.g. after a save) so the thread re-fetches. */
  refreshKey?: number;
  /** WS-Z6 — the thread is a section BODY now; its head shows this count. `null` while
   *  the first load is in flight. Same contract as `VersionHistoryPanel`. */
  onCount?: (n: number | null) => void;
}

function badgeClass(kind: CommentType): string {
  return kind === "internal" ? "badge badge--warn" : "badge badge--info";
}

export function CommentsThread({ templateId, refreshKey, onCount }: CommentsThreadProps) {
  const { client } = useServices();
  const isAdmin = useSessionStore((s) => s.isAdmin());
  const [comments, setComments] = useState<ReviewComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [commentType, setCommentType] = useState<CommentType>("public");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await client.listComments(templateId);
      setComments(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [client, templateId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!cancelled) await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [load, refreshKey]);

  // Report the count to the accordion head (see `onCount`). Keyed on the length, so a
  // re-list that returns the same thread does not re-notify.
  useEffect(() => {
    onCount?.(loading ? null : comments.length);
  }, [onCount, loading, comments.length]);

  const add = useCallback(async () => {
    const body = text.trim();
    if (!body) {
      setError("Enter a comment before posting.");
      return;
    }
    setPosting(true);
    setError(null);
    try {
      const created = await client.addComment(templateId, { commentText: body, commentType });
      setComments((prev) => [...prev, created]);
      setText("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPosting(false);
    }
  }, [client, templateId, text, commentType]);

  return (
    /* WS-Z6 / R12: the private `<details>` is gone — this is the BODY of a section in the
       editor's one `ui/Accordion.tsx`, whose head carries the label and the count. */
    <div className="tpl-editor__section-body" data-testid="comments-thread">
      {error && (
        <p className="tpl-editor__error" role="alert" data-testid="comments-error">
          <ErrorNotice detail={error} />
        </p>
      )}

      {loading ? (
        <p className="faint" data-testid="comments-loading">
          Loading comments…
        </p>
      ) : comments.length === 0 ? (
        <p className="faint" data-testid="comments-empty">
          No comments yet.
        </p>
      ) : (
        <ul className="tpl-editor__comments" data-testid="comments-list">
          {comments.map((c) => (
            <li key={c.id} className="tpl-editor__comment" data-testid="comment-row">
              <div className="tpl-editor__comment-head">
                <span className={badgeClass(c.commentType)} data-testid="comment-type">
                  {c.commentType}
                </span>
                {c.createdByRole && <span className="faint">{c.createdByRole}</span>}
                {c.templateVersionId != null && c.templateVersionId !== "" && (
                  <span className="faint">on this version</span>
                )}
                <span className="faint">{serverTsDate(c.createdAt)?.toLocaleString() ?? "—"}</span>
              </div>
              <p className="tpl-editor__comment-text">{c.commentText}</p>
            </li>
          ))}
        </ul>
      )}

      <div className="tpl-editor__comment-form" data-testid="comment-form">
        <textarea
          className="input"
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a review comment…"
          data-testid="comment-input"
        />
        <div className="row tpl-editor__comment-controls">
          {isAdmin && (
            <select
              className="input"
              value={commentType}
              onChange={(e) => setCommentType(e.target.value as CommentType)}
              aria-label="Comment visibility"
              data-testid="comment-visibility"
            >
              <option value="public">Public (visible to the client)</option>
              <option value="internal">Internal (admins only)</option>
            </select>
          )}
          <button
            type="button"
            /* F-41 / LB-8: posting a review comment is a form commit inside a collapsed
               section, not the editor's lifecycle action. One volt fill per view, and in
               the template editor that one is Publish. */
            className="btn btn--sm"
            onClick={() => void add()}
            disabled={posting}
            data-testid="comment-add"
          >
            {posting ? "Posting…" : "Add comment"}
          </button>
        </div>
      </div>
    </div>
  );
}
